import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { en_config } from '../../config';

interface Props {
    email: string;
    onVerify: (code: string[]) => void;
    onResend: () => void | Promise<unknown>;
    onBack: () => void;
    loading?: boolean;
    resendInProgress?: boolean;
}

export const CampaignApplicationOTP: React.FC<Props> = ({
    email,
    onVerify,
    onResend,
    onBack,
    loading,
    resendInProgress,
}) => {
    const { themeStyle } = useThemeContext();
    const t = theme[themeStyle];
    const codeLength = en_config.TWO_FA_LENGTH ?? 6;
    const [otp, setOtp] = useState<string[]>(new Array(codeLength).fill(''));
    const [activeIndex, setActiveIndex] = useState(0);
    const [seconds, setSeconds] = useState(30);
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

    useEffect(() => {
        inputRefs.current[activeIndex]?.focus();
    }, [activeIndex]);

    useEffect(() => {
        if (seconds <= 0) return;
        const timer = setInterval(() => setSeconds(s => s - 1), 1000);
        return () => clearInterval(timer);
    }, [seconds]);

    const handleChange = (index: number, value: string) => {
        const digit = value.slice(-1);
        const next = [...otp];
        next[index] = digit;
        setOtp(next);
        if (digit && index < codeLength - 1) setActiveIndex(index + 1);
    };

    const handleKeyDown = (index: number, key: string) => {
        if (key === 'Backspace' && !otp[index] && index > 0) setActiveIndex(index - 1);
    };

    const handlePaste = useCallback(
        (e: React.ClipboardEvent) => {
            e.preventDefault();
            const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, codeLength);
            if (!text.length) return;
            const next = new Array(codeLength).fill('');
            text.split('').forEach((ch, i) => {
                next[i] = ch;
            });
            setOtp(next);
            setActiveIndex(Math.min(text.length, codeLength - 1));
        },
        [codeLength],
    );

    const handleResend = () => {
        if (resendInProgress || seconds > 0) return;
        const result = onResend();
        if (result && typeof (result as Promise<unknown>).then === 'function') {
            (result as Promise<unknown>).then(() => setSeconds(30)).catch(() => {});
        } else {
            setSeconds(30);
        }
    };

    const allFilled = otp.every(d => d !== '');

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2 }}>
            <Button
                startIcon={<ArrowBackIcon />}
                onClick={onBack}
                sx={{
                    alignSelf: 'flex-start',
                    color: t.highlight,
                    textTransform: 'none',
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    fontSize: 14,
                    mb: 4,
                    '&:hover': { bgcolor: 'rgba(255,201,101,0.08)' },
                }}>
                Back
            </Button>

            <Box sx={{ textAlign: 'center', mb: 5 }}>
                <Typography
                    sx={{
                        fontSize: { xs: 28, sm: 36 },
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontWeight: 800,
                        color: t.true_white,
                        mb: 1.5,
                    }}>
                    Verify your email
                </Typography>
                <Typography sx={{ color: t.subtle_white, fontSize: 16, lineHeight: 1.5 }}>
                    We just sent a verification code to <strong style={{ color: t.highlight }}>{email}</strong>
                </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: { xs: 1.5, sm: 2 }, mb: 5 }} onPaste={handlePaste}>
                {otp.map((digit, i) => (
                    <Box
                        key={i}
                        component="input"
                        type="text"
                        inputMode="numeric"
                        autoComplete={i === 0 ? 'one-time-code' : 'off'}
                        value={digit}
                        ref={(el: HTMLInputElement | null) => {
                            inputRefs.current[i] = el;
                        }}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(i, e.target.value)}
                        onKeyDown={(e: React.KeyboardEvent) => handleKeyDown(i, e.key)}
                        sx={{
                            width: { xs: 44, sm: 56 },
                            height: { xs: 56, sm: 68 },
                            borderRadius: 2,
                            border: digit ? `2px solid ${t.highlight}` : `1px solid ${t.grey}40`,
                            bgcolor: t.wrapperColor,
                            color: t.true_white,
                            fontSize: { xs: 24, sm: 30 },
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            textAlign: 'center',
                            outline: 'none',
                            transition: 'border 0.2s',
                            '&:focus': {
                                border: `2px solid ${t.highlight}`,
                                boxShadow: `0 0 0 3px ${t.highlight}20`,
                            },
                        }}
                    />
                ))}
            </Box>

            <Button
                onClick={() => onVerify(otp)}
                disabled={!allFilled || loading}
                fullWidth
                sx={{
                    maxWidth: 400,
                    py: 2.5,
                    borderRadius: 10,
                    background: `linear-gradient(90deg, ${t.highlight}, ${t.primary_orange})`,
                    color: t.button_text,
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    fontWeight: 800,
                    fontSize: 18,
                    textTransform: 'none',
                    boxShadow: `0 10px 30px ${t.highlight}30`,
                    '&:hover': { boxShadow: `0 15px 40px ${t.highlight}45` },
                    '&:active': { transform: 'scale(0.95)' },
                    transition: 'all 0.3s',
                    '&.Mui-disabled': { opacity: 0.5 },
                    mb: 3,
                }}>
                {loading ? <CircularProgress size={24} sx={{ color: t.button_text }} /> : 'Verify'}
            </Button>

            <Typography sx={{ fontSize: 14, color: t.grey, textAlign: 'center' }}>
                {seconds > 0 ? (
                    <>
                        Request new code in{' '}
                        <span style={{ color: t.highlight }}>(00:{seconds.toString().padStart(2, '0')})</span>
                    </>
                ) : (
                    <>
                        Didn&apos;t get the code?{' '}
                        {resendInProgress ? (
                            <CircularProgress size={14} sx={{ color: t.highlight }} />
                        ) : (
                            <Typography
                                component="span"
                                onClick={handleResend}
                                sx={{
                                    color: t.highlight,
                                    cursor: 'pointer',
                                    fontWeight: 600,
                                    fontSize: 14,
                                }}>
                                Resend
                            </Typography>
                        )}
                    </>
                )}
            </Typography>
        </Box>
    );
};
